/**
 * SHT-mediated third-party-app consent flow state machine.
 *
 * Per ADR-027 §1, the consent flow is an OAuth-equivalent 8-step
 * flow with SHT as the authorization server. The PHG web client
 * is responsible for steps 2-6 (PHG-side) of the 8-step flow:
 *
 *   1. App initiates (deep link / web redirect / Android intent /
 *      SDK call) — outside this client; arrives as
 *      `ConsentFlowInitiationParams`
 *   2. Hand-off to PHG — initial state of this flow; if patient
 *      unauthenticated, PHG re-authenticates per PFO §2 first
 *   3. Consent presentation (verified app identity, scope, purpose,
 *      retention, revocation, context) — this state machine's
 *      app-identity / scope-review / retention-review /
 *      tier-disclosure / confirmation steps
 *   4. Patient decision (grant / deny / modify scope / shorten
 *      retention) — affirmative grant required; silence/dismissal
 *      ≠ grant per ADR-027 §1 step 4
 *   5. Consent Artifact issuance under Patient-Directed Use
 *      Authority — `issuing-artifact` state
 *   6. Hand-back with auth token — `granted` (success) or `denied`
 *     (deny) terminal; the actual hand-back is performed by the
 *     orchestrator emitting a callback the app shell wires to the
 *     deep-link return / web-redirect URL
 *
 *   7. App accesses substrate APIs under token — outside this flow
 *   8. Revocation — outside this flow (handled by the active-
 *     authorizations view)
 *
 * **Doctrinal posture (load-bearing).**
 *
 *   - **Hand-off / hand-back is the architectural seam.** Step 2
 *     (hand-off TO PHG) is implicit in the flow's initial state;
 *     step 6 (hand-back with auth token) is the orchestrator's
 *     terminal callback. External apps NEVER render the consent UI
 *     in their own surface — that's a decertification trigger per
 *     ADR-027 §3. This state machine + the corresponding view layer
 *     enforce the seam structurally.
 *   - **No silent / dismissal grant.** ADR-027 §1 step 4: "the
 *     substrate does not interpret silence, dismissal, navigation-
 *     away, or partial interaction as grant. Granting requires
 *     explicit affirmative action." The `confirmation` state has
 *     two affirmative-action transitions (grant vs. deny); no
 *     timer-based default; no implicit advance.
 *   - **Patient may narrow scope or shorten retention.** ADR-027 §1
 *     step 4: "The patient may also modify the requested scope
 *     before granting (granting a subset of what was requested) or
 *     set a shorter retention period than requested." The state
 *     machine accepts in-place narrowing on `scope-review` and
 *     `retention-review` steps before reaching `confirmation`.
 *   - **Tier-aware rendering.** BMA v4.0 §2.2.4 distinguishes Tier
 *     1 (basic), Tier 2 (higher-risk audit surcharge), and Tier 3
 *     (Patient-Directed Use Authority certification). The
 *     `tier-disclosure` step renders tier-specific framing — Tier 3
 *     surfaces the Use Authority certification reference + audit
 *     cadence + retained-derived-data scope; Tier 2 surfaces
 *     sensitive-category framing; Tier 1 is a no-op step.
 *   - **Verified app identity is fetched, not app-claimed.**
 *     ADR-027 §1 step 3 commits PHG to render verified registered
 *     identity. The orchestrator fetches the app metadata from the
 *     registry surface; an unregistered or inactive app cannot
 *     proceed past `loading-app-metadata` and routes to error.
 *
 * **Patient-Facing Terms v1.3 doctrinal-copy gate** (coord issue
 * #42). Consent presentation copy is placeholder until v1.3 lands;
 * views structured so the swap is mechanical.
 */
export const CONSENT_TERMINAL_STATES = new Set([
    'granted',
    'denied',
    'cancelled',
    'error',
]);
export function isTerminal(state) {
    return CONSENT_TERMINAL_STATES.has(state.kind);
}
// ---------- Transitions ----------
export class ConsentTransitionError extends Error {
    constructor(currentKind, eventKind) {
        super(`invalid consent transition: cannot apply event "${eventKind}" in state "${currentKind}"`);
        this.name = 'ConsentTransitionError';
    }
}
export function applyEvent(state, event) {
    if (!isTerminal(state)) {
        if (event.kind === 'cancel') {
            return { kind: 'cancelled', reason: event.reason };
        }
        if (event.kind === 'error') {
            return { kind: 'error', code: event.code, message: event.message };
        }
    }
    switch (state.kind) {
        case 'loading-app-metadata':
            if (event.kind === 'app-metadata-loaded') {
                // Reject inactive apps at the boundary — defense in depth.
                if (event.app.status !== 'active') {
                    return {
                        kind: 'error',
                        code: 'app-not-active',
                        message: `app ${event.app.appIdentity} is not active in the substrate registry`,
                    };
                }
                // Verified app identity replaces app-claimed identity for
                // downstream rendering. Narrowing is initialized at the
                // continue-from-app-identity transition (app-identity-review
                // doesn't carry it; it's a read-only review step).
                return {
                    kind: 'app-identity-review',
                    initiation: state.initiation,
                    app: event.app,
                };
            }
            break;
        case 'app-identity-review':
            if (event.kind === 'continue-from-app-identity-review') {
                return {
                    kind: 'scope-review',
                    initiation: state.initiation,
                    app: state.app,
                    narrowing: {
                        dataScope: state.initiation.requestedDataScope,
                        operationalScope: state.initiation.requestedOperationalScope,
                        retentionDiscipline: state.initiation.requestedRetentionDiscipline,
                        timeBounds: state.initiation.requestedTimeBounds,
                    },
                };
            }
            break;
        case 'scope-review':
            if (event.kind === 'narrow-scope') {
                // PL4 Constitutional Tier A severity — scope-intersection
                // enforcement canonical per ADR-027 §1 step 4 doctrinal-invariant:
                // "The patient may also modify the requested scope before granting
                // (granting a subset of what was requested)." Narrow-scope events
                // MUST be a subset of the original requested scope; intersect
                // `event.dataScope`/`operationalScope` against
                // `state.initiation.requestedDataScope`/`requestedOperationalScope`
                // (defense-in-depth at reducer canonical; adversarial caller cannot
                // broaden scope by passing elements outside the original request).
                // Patient-fiduciary doctrine Six Commitments v1.2 #4 substantively-
                // load-bearing closure per CTO third-pass security re-scan §1.5.
                return {
                    kind: 'scope-review',
                    initiation: state.initiation,
                    app: state.app,
                    narrowing: {
                        ...state.narrowing,
                        dataScope: event.dataScope.filter((s) => state.initiation.requestedDataScope.includes(s)),
                        operationalScope: event.operationalScope.filter((s) => state.initiation.requestedOperationalScope.includes(s)),
                    },
                };
            }
            if (event.kind === 'continue-from-scope-review') {
                return {
                    kind: 'retention-review',
                    initiation: state.initiation,
                    app: state.app,
                    narrowing: state.narrowing,
                };
            }
            break;
        case 'retention-review':
            if (event.kind === 'narrow-retention') {
                return {
                    kind: 'retention-review',
                    initiation: state.initiation,
                    app: state.app,
                    narrowing: {
                        ...state.narrowing,
                        retentionDiscipline: event.retentionDiscipline,
                        timeBounds: event.timeBounds,
                    },
                };
            }
            if (event.kind === 'continue-from-retention-review') {
                return {
                    kind: 'tier-disclosure',
                    initiation: state.initiation,
                    app: state.app,
                    narrowing: state.narrowing,
                };
            }
            break;
        case 'tier-disclosure':
            if (event.kind === 'continue-from-tier-disclosure') {
                return {
                    kind: 'confirmation',
                    initiation: state.initiation,
                    app: state.app,
                    narrowing: state.narrowing,
                };
            }
            break;
        case 'confirmation':
            if (event.kind === 'grant') {
                return {
                    kind: 'issuing-artifact',
                    initiation: state.initiation,
                    app: state.app,
                    narrowing: state.narrowing,
                };
            }
            if (event.kind === 'deny') {
                return {
                    kind: 'denied',
                    initiation: state.initiation,
                    app: state.app,
                };
            }
            break;
        case 'issuing-artifact':
            if (event.kind === 'artifact-issued') {
                return {
                    kind: 'granted',
                    initiation: state.initiation,
                    app: state.app,
                    artifact: event.artifact,
                };
            }
            break;
        case 'granted':
        case 'denied':
        case 'cancelled':
        case 'error':
            break;
    }
    throw new ConsentTransitionError(state.kind, event.kind);
}
export function initialConsentState(initiation) {
    return { kind: 'loading-app-metadata', initiation };
}
//# sourceMappingURL=consent-state.js.map